import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ConsumerLayout from './ConsumerLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Store, Search, MapPin, Clock, Phone, Heart, Share2,
  ChevronLeft, ShoppingBag, Loader2, Package, SlidersHorizontal,
  Percent, ArrowUpDown, X
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import ProductCard, { ProductCardGrid, ProductCardSkeleton, EmptyProductState } from './ProductCard';

// Debounce hook for search
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Text highlighter component
function HighlightText({ text, highlight }) {
  if (!highlight || !text) return <>{text}</>;
  
  const parts = text.split(new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  
  return (
    <>
      {parts.map((part, i) => 
        part.toLowerCase() === highlight.toLowerCase() ? (
          <mark key={i} className="bg-yellow-200 text-yellow-900 px-0.5 rounded">{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

export default function StoreStorefront({ user, onLogout }) {
  const { storeSlug } = useParams();
  const navigate = useNavigate();
  
  const [store, setStore] = useState(null);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('discount');
  
  // Debounced search - triggers 300ms after user stops typing
  const debouncedSearch = useDebounce(searchQuery, 300);

  useEffect(() => {
    loadStorefront();
    // Load existing cart
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, [storeSlug]);

  useEffect(() => {
    // Reload when filters change (including debounced search)
    if (store) {
      loadStorefront();
    }
  }, [selectedCategory, sortBy, debouncedSearch]);

  const loadStorefront = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory && selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      if (debouncedSearch) {
        params.append('search', debouncedSearch);
      }
      params.append('sort_by', sortBy);
      
      const response = await api.get(`/store/${storeSlug}/storefront?${params.toString()}`);
      const data = response.data;
      
      setStore(data.store);
      setItems(data.items || []);
      setCategories(data.categories || []);
      setStats(data.stats || {});
    } catch (error) {
      console.error('Failed to load storefront:', error);
      if (error.response?.status === 404) {
        toast.error('Store not found');
        navigate('/store');
      } else {
        toast.error('Failed to load store');
      }
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const addToCart = (item) => {
    const existingItem = cart.find((c) => c.rshd_id === item.id);
    let newCart;
    
    if (existingItem) {
      newCart = cart.map((c) =>
        c.rshd_id === item.id ? { ...c, quantity: c.quantity + 1 } : c
      );
    } else {
      newCart = [...cart, { 
        rshd_id: item.id, 
        name: item.name, 
        price: item.deal_price, 
        quantity: 1,
        regular_price: item.regular_price || item.deal_price * 2,
        discount_level: item.discount_level || 1,
        store_name: store?.store_name || store?.name
      }];
    }
    
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
    toast.success(`${item.name} added to cart`);
  };

  const goToCheckout = () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    localStorage.setItem('cart', JSON.stringify(cart));
    navigate('/consumer/checkout');
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${store?.store_name || store?.name} on DealShaq`,
          text: `Check out deals from ${store?.store_name || store?.name}!`,
          url: url
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading && !store) {
    return (
      <ConsumerLayout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </ConsumerLayout>
    );
  }

  if (!store) {
    return (
      <ConsumerLayout user={user} onLogout={onLogout}>
        <div className="text-center py-12">
          <Store className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Store Not Found</h2>
          <Button onClick={() => navigate('/store')} variant="outline">
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Stores
          </Button>
        </div>
      </ConsumerLayout>
    );
  }

  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <ConsumerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/store')}
          className="text-gray-600 hover:text-gray-900 -ml-2"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          All Stores
        </Button>

        {/* Store Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            {/* Store Logo */}
            <div className="bg-white rounded-xl p-3 w-20 h-20 flex items-center justify-center shadow-md flex-shrink-0">
              {store.logo_url ? (
                <img 
                  src={store.logo_url} 
                  alt={store.store_name || store.name}
                  className="w-14 h-14 object-contain"
                />
              ) : (
                <Store className="w-10 h-10 text-emerald-600" />
              )}
            </div>

            {/* Store Info */}
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                {store.store_name || store.name}
              </h1>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-emerald-50 text-sm">
                {store.address && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {store.address}
                  </span>
                )}
                {store.hours && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {store.hours}
                  </span>
                )}
                {store.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    {store.phone}
                  </span>
                )}
              </div>
              
              {/* Charity Badge */}
              {store.charity && (
                <div className="mt-3">
                  <Badge className="bg-white/20 text-white border-white/30">
                    <Heart className="w-3 h-3 mr-1 fill-current" />
                    Supporting {store.charity}
                  </Badge>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button 
                variant="secondary" 
                size="sm"
                onClick={handleShare}
                className="bg-white/20 hover:bg-white/30 text-white border-0"
              >
                <Share2 className="w-4 h-4" />
              </Button>
              {cartItemCount > 0 && (
                <Button 
                  onClick={goToCheckout}
                  className="bg-white text-emerald-600 hover:bg-emerald-50"
                >
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Cart ({cartItemCount})
                </Button>
              )}
            </div>
          </div>

          {/* Stats Row */}
          <div className="flex gap-6 mt-4 pt-4 border-t border-white/20">
            <div>
              <span className="text-2xl font-bold">{stats.total_deals || 0}</span>
              <span className="text-emerald-100 text-sm ml-1">Deals</span>
            </div>
            <div>
              <span className="text-2xl font-bold">{stats.avg_discount || 0}%</span>
              <span className="text-emerald-100 text-sm ml-1">Avg Discount</span>
            </div>
            <div>
              <span className="text-2xl font-bold">{stats.categories_count || 0}</span>
              <span className="text-emerald-100 text-sm ml-1">Categories</span>
            </div>
          </div>
        </div>

        {/* Filters Bar */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search this store..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-10"
                  data-testid="storefront-search"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Category Filter */}
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full sm:w-[180px]" data-testid="category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Sort */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full sm:w-[160px]" data-testid="sort-filter">
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="discount">Highest Discount</SelectItem>
                  <SelectItem value="price_low">Price: Low to High</SelectItem>
                  <SelectItem value="price_high">Price: High to Low</SelectItem>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="expiring">Expiring Soon</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Search Results Info */}
            {debouncedSearch && (
              <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                <Search className="w-4 h-4" />
                <span>
                  Showing results for "<span className="font-medium text-gray-900">{debouncedSearch}</span>"
                  {items.length > 0 && ` (${items.length} found)`}
                </span>
                <button 
                  onClick={clearSearch}
                  className="text-emerald-600 hover:text-emerald-700 font-medium ml-2"
                >
                  Clear
                </button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category Pills */}
        {categories.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory('all')}
              className={selectedCategory === 'all' ? 'bg-emerald-600' : ''}
            >
              All
            </Button>
            {categories.map((cat) => (
              <Button
                key={cat}
                variant={selectedCategory === cat ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(cat)}
                className={`whitespace-nowrap ${selectedCategory === cat ? 'bg-emerald-600' : ''}`}
              >
                {cat}
              </Button>
            ))}
          </div>
        )}

        {/* Products Grid */}
        {loading ? (
          <ProductCardGrid>
            {[1,2,3,4,5,6,7,8].map(i => <ProductCardSkeleton key={i} />)}
          </ProductCardGrid>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Deals Available</h3>
              <p className="text-gray-500">
                {selectedCategory !== 'all' 
                  ? `No ${selectedCategory} deals right now. Try a different category.`
                  : debouncedSearch
                    ? `No results for "${debouncedSearch}"`
                    : 'This store hasn\'t posted any deals yet. Check back later!'
                }
              </p>
              {(selectedCategory !== 'all' || debouncedSearch) && (
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => {
                    setSelectedCategory('all');
                    setSearchQuery('');
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <ProductCardGrid>
            {items.map((item) => (
              <ProductCard
                key={item.id}
                item={item}
                onAddToCart={() => addToCart(item)}
                showStoreName={false}
                highlightText={debouncedSearch}
              />
            ))}
          </ProductCardGrid>
        )}

        {/* Floating Cart Button (Mobile) */}
        {cartItemCount > 0 && (
          <div className="fixed bottom-20 left-4 right-4 md:hidden z-40">
            <Button 
              onClick={goToCheckout}
              className="w-full bg-emerald-600 hover:bg-emerald-700 shadow-lg py-6"
            >
              <ShoppingBag className="w-5 h-5 mr-2" />
              View Cart ({cartItemCount} items)
            </Button>
          </div>
        )}
      </div>
    </ConsumerLayout>
  );
}
